import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Camera, X, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useAvatars, computeInitials, colorFromString } from "@/context/AvatarContext";

/**
 * One-time avatar-upload prompt on first login.
 * Also usable as an "always available" upload/change modal by passing `forceOpen`.
 */
export default function AvatarPromptModal({ forceOpen = false, onClose }) {
    const { user, refreshUser } = useAuth();
    const { refresh: refreshAvatars } = useAvatars();
    const [open, setOpen] = useState(false);
    const [file, setFile] = useState(null);
    const [dataUrl, setDataUrl] = useState(null);
    const [saving, setSaving] = useState(false);
    const inputRef = useRef(null);

    useEffect(() => {
        if (forceOpen) {
            setOpen(true);
            return;
        }
        // Auto-open ONLY on first login (avatar_prompted stays false). If we
        // opened before /me caught up and the user IS already prompted, close.
        if (user && !user.avatar_prompted && !user.avatar_url) {
            setOpen(true);
        } else if (user && user.avatar_prompted) {
            setOpen(false);
        }
    }, [user, forceOpen]);

    const close = () => {
        setOpen(false);
        setFile(null);
        setDataUrl(user?.avatar_url || null);
        if (onClose) onClose();
    };

    const initials = computeInitials(user?.name || user?.email || "");
    const color = colorFromString(user?.email || user?.name || "");
    const previewImg = dataUrl || user?.avatar_url;

    const onPickFile = async (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        if (!f.type.startsWith("image/")) {
            toast.error("Please choose an image file.");
            return;
        }
        setFile(f);
        try {
            const compressed = await compressToDataUrl(f, 128, 0.82);
            setDataUrl(compressed);
        } catch (err) {
            toast.error("Couldn't process that image. Try another one.");
        }
    };

    const save = async () => {
        if (!dataUrl) return;
        setSaving(true);
        try {
            await api.post("/auth/me/avatar", { data_url: dataUrl });
            toast.success("Profile photo saved.");
            await refreshUser();
            await refreshAvatars();
            close();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Upload failed.");
        } finally {
            setSaving(false);
        }
    };

    const skip = async () => {
        setSaving(true);
        try {
            await api.post("/auth/me/avatar/skip");
            await refreshUser();
            close();
        } catch (_e) {
            close();
        } finally {
            setSaving(false);
        }
    };

    const remove = async () => {
        setSaving(true);
        try {
            await api.delete("/auth/me/avatar");
            toast.info("Profile photo removed.");
            setDataUrl(null);
            setFile(null);
            await refreshUser();
            await refreshAvatars();
        } catch (_e) {
            toast.error("Remove failed.");
        } finally {
            setSaving(false);
        }
    };

    return (
        <AnimatePresence>
            {open && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="fixed inset-0 z-[90] flex items-center justify-center bg-black/80 backdrop-blur-md"
                    data-testid="avatar-prompt-modal"
                >
                    <motion.div
                        initial={{ y: 16, scale: 0.98 }}
                        animate={{ y: 0, scale: 1 }}
                        exit={{ y: 16, scale: 0.98 }}
                        className="w-[440px] max-w-[92vw] bg-[#0A0A0A] border-2 border-virtus-orange"
                    >
                        <div className="px-5 py-3 border-b border-virtus-line flex items-center justify-between">
                            <div className="font-mono text-[10px] tracking-[0.28em] uppercase text-virtus-orange">
                                {forceOpen ? "PROFILE PHOTO" : "SET YOUR PROFILE PHOTO"}
                            </div>
                            {forceOpen && (
                                <button
                                    onClick={close}
                                    className="text-virtus-dim hover:text-virtus-orange"
                                    data-testid="avatar-prompt-close-btn"
                                >
                                    <X size={14} />
                                </button>
                            )}
                        </div>
                        <div className="p-6 space-y-5">
                            <p className="text-sm text-virtus-dim leading-relaxed">
                                Show up as your photo on every task you own. If you skip, your
                                initials will be used instead — you can always change this later.
                            </p>

                            <div className="flex items-center justify-center py-4">
                                {previewImg ? (
                                    <img
                                        src={previewImg}
                                        alt={user?.name}
                                        style={{
                                            width: 120,
                                            height: 120,
                                            borderRadius: "50%",
                                            objectFit: "cover",
                                            border: "2px solid #FF5500",
                                        }}
                                        data-testid="avatar-prompt-preview"
                                    />
                                ) : (
                                    <div
                                        style={{
                                            width: 120,
                                            height: 120,
                                            borderRadius: "50%",
                                            background: `linear-gradient(135deg, ${color}, ${color}CC)`,
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            fontFamily: "monospace",
                                            fontSize: 42,
                                            fontWeight: 800,
                                            color: "#000",
                                            border: "2px solid #FF5500",
                                        }}
                                        data-testid="avatar-prompt-initials"
                                    >
                                        {initials}
                                    </div>
                                )}
                            </div>

                            <input
                                ref={inputRef}
                                type="file"
                                accept="image/*"
                                className="hidden"
                                onChange={onPickFile}
                                data-testid="avatar-prompt-file-input"
                            />
                            <div className="flex items-center justify-center gap-2">
                                <button
                                    onClick={() => inputRef.current?.click()}
                                    disabled={saving}
                                    className="flex items-center gap-2 font-mono text-[10px] tracking-[0.28em] uppercase border border-virtus-line text-white px-3 py-2 hover:border-virtus-orange hover:text-virtus-orange disabled:opacity-40"
                                    data-testid="avatar-prompt-choose-btn"
                                >
                                    <Camera size={11} />
                                    {previewImg ? "REPLACE" : "CHOOSE PHOTO"}
                                </button>
                                {(user?.avatar_url || dataUrl) && (
                                    <button
                                        onClick={remove}
                                        disabled={saving}
                                        className="flex items-center gap-2 font-mono text-[10px] tracking-[0.28em] uppercase border border-virtus-line text-virtus-dim px-3 py-2 hover:border-virtus-stuck hover:text-virtus-stuck disabled:opacity-40"
                                        data-testid="avatar-prompt-remove-btn"
                                    >
                                        <Trash2 size={11} /> REMOVE
                                    </button>
                                )}
                            </div>

                            <div className="text-[10px] text-virtus-faint text-center font-mono tracking-[0.16em]">
                                Auto-cropped square · resized to 128×128 · &lt;30KB
                            </div>
                        </div>
                        <div className="flex items-center justify-between px-5 py-3 border-t border-virtus-line">
                            <button
                                onClick={skip}
                                disabled={saving}
                                className="font-mono text-[10px] tracking-[0.28em] uppercase text-virtus-dim hover:text-white px-3 py-2 disabled:opacity-40"
                                data-testid="avatar-prompt-skip-btn"
                            >
                                {forceOpen ? "CANCEL" : "SKIP FOR NOW"}
                            </button>
                            <button
                                onClick={save}
                                disabled={saving || !dataUrl || dataUrl === user?.avatar_url}
                                className="flex items-center gap-2 font-mono text-[11px] tracking-[0.28em] uppercase bg-virtus-orange text-black px-4 py-2 hover:bg-white disabled:opacity-40 glow-orange"
                                data-testid="avatar-prompt-save-btn"
                            >
                                {saving ? <Loader2 size={12} className="animate-spin" /> : <Camera size={12} />}
                                SAVE PHOTO
                            </button>
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}

// -----------------------------------------------------------------------------
// Client-side compression to square JPEG data URL
// -----------------------------------------------------------------------------

async function compressToDataUrl(file, size = 128, quality = 0.85) {
    const bitmap = await createBitmap(file);
    const minSide = Math.min(bitmap.width, bitmap.height);
    const sx = (bitmap.width - minSide) / 2;
    const sy = (bitmap.height - minSide) / 2;
    const canvas = document.createElement("canvas");
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(bitmap, sx, sy, minSide, minSide, 0, 0, size, size);
    return canvas.toDataURL("image/jpeg", quality);
}

function createBitmap(file) {
    if ("createImageBitmap" in window) {
        return window.createImageBitmap(file);
    }
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = reject;
        img.src = URL.createObjectURL(file);
    });
}
